import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Spinner } from '@/ui';
import { NAV_ITEMS } from './navigation';
import { screens } from './screens';
import { RequirePermission } from './permissions';
import { AppLayout } from './AppLayout';

/**
 * Routing.
 *
 * Screens are code-split — the loaders and the reasoning live in `screens.ts`.
 * A nav entry without a screen there renders the "not built yet" placeholder.
 */

function NotBuiltYet({ title }: { title: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1 text-center">
      <p className="text-base font-medium text-ink">{title} has not been ported yet</p>
      <p className="text-xs text-muted">
        See docs/migration.md for the porting order and the pattern to follow.
      </p>
    </div>
  );
}

function PageFallback() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Spinner size={18} className="text-faint" />
    </div>
  );
}

export function AppRoutes({ onSignOut }: { onSignOut: () => void }) {
  return (
    <Routes>
      <Route element={<AppLayout onSignOut={onSignOut} />}>
        {NAV_ITEMS.map((item) => {
          const Screen = screens[item.id];
          return (
            <Route
              key={item.id}
              path={item.path}
              element={
                <RequirePermission permissions={item.permissions}>
                  {/* ── `key` is what makes the loader appear on the click ────
                      react-router v7 puts every navigation inside
                      `startTransition`, and a transition's whole purpose is to
                      *not* replace visible content with a fallback: if the
                      screen being navigated to suspends, React holds the screen
                      you are leaving on the glass until the new one is ready.
                      With a lazily-loaded screen that means the old page sits
                      there, apparently ignoring the click, for as long as the
                      chunk takes — and the spinner only ever appeared after it
                      landed, which is the moment it stopped being useful.

                      React only exempts a Suspense boundary that is *newly
                      mounted*, since there is nothing of its own to preserve.
                      Routes render at the same position in the tree, so without
                      a key React reuses one boundary across all of them and the
                      exemption never applies. Keying it per screen tears the
                      boundary down and builds a new one on each navigation, so
                      the fallback shows immediately.

                      Most navigations should never reach it: the sidebar
                      prefetches on hover (see screens.ts), so the chunk is
                      usually in memory before the click. This is the honest
                      answer for the times it is not — a cold cache, a slow
                      line, or arriving by keyboard. */}
                  <Suspense key={item.id} fallback={<PageFallback />}>
                    {Screen ? <Screen /> : <NotBuiltYet title={item.id} />}
                  </Suspense>
                </RequirePermission>
              }
            />
          );
        })}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
